import { useEffect, useRef } from 'react';
import JsBarcode from 'jsbarcode';

// TSC TE244 — 75mm × 50mm roll, driver portrait: 50mm wide × 75mm tall
// Prints 2 identical labels per click — one above, one below on the roll

export const IndoorRegistrationTSCLabelPrint = ({ data }) => {
  const barcodeRef1 = useRef(null);
  const barcodeRef2 = useRef(null);

  useEffect(() => {
    if (!data) return;

    const barcodeValue = String(data.uhid || data.ipd_no || 'N/A');
    const barcodeOptions = {
      format: 'CODE128',
      displayValue: true,
      fontSize: 8,
      textMargin: 1,
      height: 22,
      width: 1.0,
      margin: 1,
    };

    if (barcodeRef1.current) JsBarcode(barcodeRef1.current, barcodeValue, barcodeOptions);
    if (barcodeRef2.current) JsBarcode(barcodeRef2.current, barcodeValue, barcodeOptions);

    const timer = setTimeout(() => window.print(), 600);
    return () => clearTimeout(timer);
  }, [data]);

  function formatDate(dateString) {
    if (!dateString) return '';
    const d = new Date(dateString);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  }

  if (!data) return null;

  const patientName =
    `${data.patient?.name_suffix || ''} ${data.patient?.first_name || ''} ${data.patient?.last_name || ''}`.trim();

  const wardRoom = [
    data.consultant_detail?.room?.ward?.name,
    data.consultant_detail?.room?.room_no,
  ]
    .filter(Boolean)
    .join(' / ');

  const ROW = {
    display: 'flex',
    marginBottom: '0.7mm',
    fontSize: '7.5pt',
    lineHeight: '1.25',
  };

  const LABEL_STYLE = {
    fontWeight: 'bold',
    minWidth: '11mm',
    flexShrink: 0,
    color: '#222',
  };

  const VALUE = {
    flex: 1,
    fontWeight: 'bold',
    color: '#000',
    wordBreak: 'break-word',
  };

  const labelContent = (barcodeRef) => (
    <div
      className="tsc-label"
      style={{
        width: '50mm',
        height: 'auto',
        padding: '1.5mm 2mm',
        fontFamily: 'Arial, Helvetica, sans-serif',
        fontWeight: 'bold',
        background: 'white',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* ── Patient Name ─────────────────────────────── */}
      <div style={{
        fontWeight: 'bold',
        fontSize: '8.5pt',
        borderBottom: '0.6pt solid #000',
        paddingBottom: '0.5mm',
        marginBottom: '0.8mm',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        letterSpacing: '0.1pt',
      }}>
        {patientName}
      </div>

      {/* ── UHID ─────────────────────────────────────── */}
      <div style={ROW}>
        <span style={LABEL_STYLE}>UHID:</span>
        <span style={VALUE}>{data.uhid}</span>
      </div>

      {/* ── IPD No ───────────────────────────────────── */}
      <div style={ROW}>
        <span style={LABEL_STYLE}>IPD No:</span>
        <span style={VALUE}>{data.ipd_no}</span>
      </div>

      {/* ── Age | Gender ─────────────────────────────── */}
      <div style={{ ...ROW, justifyContent: 'space-between' }}>
        <span><b>Age:</b> {data.patient?.age}</span>
        <span><b>Gender:</b> {data.patient?.gender}</span>
      </div>

      {/* ── Ward / Room ───────────────────────────────── */}
      <div style={ROW}>
        <span style={LABEL_STYLE}>Ward:</span>
        <span style={{ ...VALUE, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {wardRoom}
        </span>
      </div>

      {/* ── Bed ──────────────────────────────────────── */}
      {data.consultant_detail?.bed_no && (
        <div style={ROW}>
          <span style={LABEL_STYLE}>Bed:</span>
          <span style={VALUE}>{data.consultant_detail.bed_no}</span>
        </div>
      )}

      {/* ── Doctor ───────────────────────────────────── */}
      <div style={ROW}>
        <span style={LABEL_STYLE}>Doctor:</span>
        <span style={{ ...VALUE, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {data.consultant_detail?.doctor?.name}
        </span>
      </div>

      {/* ── Admission Date ────────────────────────────── */}
      <div style={ROW}>
        <span style={LABEL_STYLE}>Adm:</span>
        <span style={VALUE}>{formatDate(data.date)}</span>
      </div>

      {/* ── Mobile ───────────────────────────────────── */}
      {data.patient?.address?.mobile && (
        <div style={ROW}>
          <span style={LABEL_STYLE}>Mobile:</span>
          <span style={VALUE}>{data.patient.address.mobile}</span>
        </div>
      )}

      {/* ── Barcode ───────────────────────────────────── */}
      <div style={{ textAlign: 'center', marginTop: '0.5mm' }}>
        <svg ref={barcodeRef} style={{ maxWidth: '100%', display: 'block', margin: '0 auto' }} />
      </div>
    </div>
  );

  return (
    <>
      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }

        @page {
          size: 50mm auto;
          margin: 0;
        }

        html, body {
          width: 50mm;
          height: auto;
          background: white;
        }

        @media screen {
          body {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 12px;
            min-height: 100vh;
            background: #bbb;
            padding: 20px 0;
          }
          .tsc-label {
            box-shadow: 0 3px 14px rgba(0,0,0,0.35);
          }
        }

        @media print {
          html, body { background: white; }
          .tsc-label { box-shadow: none; }
        }
      `}</style>

      {/* Label 1 */}
      {labelContent(barcodeRef1)}

      {/* Label 2 — same, directly below */}
      {labelContent(barcodeRef2)}
    </>
  );
};
